import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';

/**
 * Packages the whole production — edit list plus every source clip and audio file —
 * as a zip, so the edit can be continued in Resolve. Large productions are zipped in
 * the background, so this polls until the package is ready.
 */
export default function DownloadProjectButton({ productionId }: { productionId: string }) {
  const { t } = useTranslation('production');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const handleClick = async () => {
    setLoading(true);
    setResult(t('final.packaging'));
    try {
      for (let i = 0; i < 150; i++) {
        const resp = await apiFetch(`/api/download/${productionId}`);
        const data = await resp.json();
        if (data.status === 'ready' && data.url) {
          setResult(t('final.packagingReady'));
          window.location.href = data.url; // browser downloads straight from GCS
          return;
        }
        if (data.status === 'building') {
          setResult(t('final.packagingServer'));
          await new Promise((r) => setTimeout(r, 5000));
          continue;
        }
        setResult(t('final.errorPrefix', { reason: data.error || data.detail || t('final.noPackage') }));
        return;
      }
      setResult(t('final.packagingSlow'));
    } catch {
      setResult(t('final.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" onClick={handleClick} disabled={loading} title={t('final.zipTitle')}>
        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
        {loading ? t('final.packaging') : t('final.zip')}
      </Button>
      {result && <span className="text-xs text-muted-foreground">{result}</span>}
    </div>
  );
}
